// IMPORTANT: this module is reachable from the Electron main-process bundle
// (`src/background.ts` via `utils/navigate.ts`), which cannot bundle Vue
// components — only import type-level or component-free modules here.
import type {
  AgentRefId,
  AnyAgentId,
  AutomationId,
  AutomationRefId,
  ConversationId,
  ConversationRefId,
  OrgId,
  ReferenceId,
  TableId,
  TableRefId,
  WorkspaceId,
  WorkspaceRefId,
} from "@ctrl/controller-rpc";

import { nanoidOfPrefixedId } from "@/utils/nanoid";

/**
 * Compose a workspace reference id: `workspace_ref:<org-nanoid>:<ws-nanoid>`.
 *
 * This mirrors the controller's encoding (`Workspace_ref.to_string` in
 * `api/schemas/src/ids.ml`) because the RPC surface only exposes the reverse
 * direction (`controller.id.workspaceRef.parse`). Returns null when either
 * input is not a canonical `org:<nanoid>` / `workspace:<nanoid>` id.
 */
export function makeWorkspaceRefId(
  orgId: OrgId,
  workspaceId: WorkspaceId
): WorkspaceRefId | null {
  const orgNanoid = nanoidOfPrefixedId("org", orgId);
  const workspaceNanoid = nanoidOfPrefixedId("workspace", workspaceId);
  if (!orgNanoid || !workspaceNanoid) return null;
  return `workspace_ref:${orgNanoid}:${workspaceNanoid}`;
}

/** The typed reference to a workspace, as used by reference nodes and RPCs. */
export function makeWorkspaceReference(
  orgId: OrgId,
  workspaceId: WorkspaceId
): ReferenceId | null {
  const id = makeWorkspaceRefId(orgId, workspaceId);
  return id ? { kind: "workspace_ref", id } : null;
}

/**
 * Compose a table (whole-database) reference id:
 * `table_ref:<workspace-nanoid>:<table-nanoid>` (see `Table_ref` in
 * `api/schemas/src/ids.ml`; the controller only exposes the reverse
 * direction, `controller.id.tableRef.parse`). Returns null when either input
 * is not a canonical `workspace:<nanoid>` / `table:<nanoid>` id.
 */
export function makeTableRefId(
  workspaceId: WorkspaceId,
  tableId: TableId
): TableRefId | null {
  const workspaceNanoid = nanoidOfPrefixedId("workspace", workspaceId);
  const tableNanoid = nanoidOfPrefixedId("table", tableId);
  if (!workspaceNanoid || !tableNanoid) return null;
  return `table_ref:${workspaceNanoid}:${tableNanoid}`;
}

/** The typed reference to a whole database, as used by reference nodes and RPCs. */
export function makeTableReference(
  workspaceId: WorkspaceId,
  tableId: TableId
): ReferenceId | null {
  const id = makeTableRefId(workspaceId, tableId);
  return id ? { kind: "table_ref", id } : null;
}

/** The typed reference to an organization, as used by reference nodes and RPCs. */
export function makeOrganizationReference(orgId: OrgId): ReferenceId | null {
  return nanoidOfPrefixedId("org", orgId)
    ? { kind: "organization", id: orgId }
    : null;
}

/**
 * The `agent:` / `assistant:` payload of an agent id, or null when the id
 * isn't a canonical `Agent_any` (`api/schemas/src/ids.ml`).
 */
function agentAnyParts(
  agentId: AnyAgentId
): { kind: "agent" | "assistant"; nanoid: string } | null {
  const agentNanoid = nanoidOfPrefixedId("agent", agentId);
  if (agentNanoid) return { kind: "agent", nanoid: agentNanoid };
  const assistantNanoid = nanoidOfPrefixedId("assistant", agentId);
  if (assistantNanoid) return { kind: "assistant", nanoid: assistantNanoid };
  return null;
}

/**
 * Compose an agent reference id:
 * `agent_ref:<org-nanoid>:agent:<agent-nanoid>` (or `assistant:`).
 * See `Agent_ref` in `api/schemas/src/ids.ml`.
 */
export function makeAgentRefId(
  orgId: OrgId,
  agentId: AnyAgentId
): AgentRefId | null {
  const orgNanoid = nanoidOfPrefixedId("org", orgId);
  const agent = agentAnyParts(agentId);
  if (!orgNanoid || !agent) return null;
  return `agent_ref:${orgNanoid}:${agent.kind}:${agent.nanoid}`;
}

export function makeAgentReference(
  orgId: OrgId,
  agentId: AnyAgentId
): ReferenceId | null {
  const id = makeAgentRefId(orgId, agentId);
  return id ? { kind: "agent", id } : null;
}

/**
 * Compose an automation reference id:
 * `automation_ref:<org-nanoid>:<automation-nanoid>`.
 * See `Automation_ref` in `api/schemas/src/ids.ml`.
 */
export function makeAutomationRefId(
  orgId: OrgId,
  automationId: AutomationId
): AutomationRefId | null {
  const orgNanoid = nanoidOfPrefixedId("org", orgId);
  const automationNanoid = nanoidOfPrefixedId("automation", automationId);
  if (!orgNanoid || !automationNanoid) return null;
  return `automation_ref:${orgNanoid}:${automationNanoid}`;
}

export function makeAutomationReference(
  orgId: OrgId,
  automationId: AutomationId
): ReferenceId | null {
  const id = makeAutomationRefId(orgId, automationId);
  return id ? { kind: "automation", id } : null;
}

/**
 * Compose a conversation reference id:
 * `conversation_ref:<org-nanoid>:agent:<agent-nanoid>:<conversation-nanoid>`.
 * See `Conversation_ref` in `api/schemas/src/ids.ml`.
 */
export function makeConversationRefId(
  orgId: OrgId,
  agentId: AnyAgentId,
  conversationId: ConversationId
): ConversationRefId | null {
  const orgNanoid = nanoidOfPrefixedId("org", orgId);
  const agent = agentAnyParts(agentId);
  const conversationNanoid = nanoidOfPrefixedId("conversation", conversationId);
  if (!orgNanoid || !agent || !conversationNanoid) return null;
  return `conversation_ref:${orgNanoid}:${agent.kind}:${agent.nanoid}:${conversationNanoid}`;
}

export function makeConversationReference(
  orgId: OrgId,
  agentId: AnyAgentId,
  conversationId: ConversationId
): ReferenceId | null {
  const id = makeConversationRefId(orgId, agentId, conversationId);
  return id ? { kind: "conversation", id } : null;
}
